import type {
    Award,
    Faq,
    Principle,
    ProcessStep,
    Service,
    TeamMember,
    Testimonial
} from '@/types'

import { media, img } from '@/constants/media'

export const team: TeamMember[] = [
    { name: 'Giuseppe Brown', role: 'CEO and Founder', image: 'https://new-ss-site.cdn.modulify.website/images/team/giuseppe-brown.png', bio: "Giuseppe co-founded Strength Scaling in Raleigh in December 2024 after watching local gyms pay big agencies for generic ads and leads that never got a text back. So he built the system they were missing: a website that ranks on Google from the start, ads that are tracked from click to booking, and a CRM that texts new leads within seconds. He treats marketing like engineering. Find the weakest link, fix it, measure it again." },
    { name: 'Peyton Carlson', role: 'CGO and Founder', image: 'https://new-ss-site.cdn.modulify.website/images/team/peyton-carlson.png', bio: "Peyton co-founded Strength Scaling and runs the growth operation from sales, partnerships, and the brand. Our first client came from a cold message Peyton kept following up on until it became a long-term partnership. He still works that way, real conversations, steady follow-up, and relationships that outlast the contract. Because of him referrals have been our best source of new clients." }
]

export const services: Service[] = [
    {
        index: '(01)',
        slug: 'full-stack-marketing',
        title: 'Full Stack Marketing.',
        price: 'Custom quote',
        description: "Paid ads, landing pages and lead follow-up run as one system, so every dollar you spend is tracked from click to closed deal.",
        delivery: 'Live in 2-3 weeks',
        includes: ['Meta and Google ad campaigns', 'High-converting landing pages', 'Lead follow-up and nurture', 'Monthly reporting and optimization'],
        image: media.services.marketing
    },
    {
        index: '(02)',
        slug: 'ai-implementation',
        title: 'AI Implementation.',
        price: 'Custom quote',
        description: "We find where AI actually saves your team time, then set it up inside the tools you already use.",
        delivery: '2-4 weeks',
        includes: ['AI opportunity audit', 'Tool selection and setup', 'Custom AI assistants', 'Team training and handover'],
        image: media.services.aiImplementation
    },
    {
        index: '(03)',
        slug: 'ai-automations',
        title: 'AI Automations.',
        price: 'Custom quote',
        description: "Repetitive work like follow-ups, booking and data entry runs on its own, so leads never go cold.",
        delivery: '1-3 weeks',
        includes: ['Instant lead response', 'CRM and calendar workflows', 'Automated follow-up sequences', 'Ongoing monitoring and fixes'],
        image: media.services.aiAutomations
    },
    {
        index: '(04)',
        slug: 'gtm-buildout',
        title: 'GTM Buildout.',
        price: 'Custom quote',
        description: "A clear plan for who you sell to, what you say and which channels bring them in, built and ready to launch.",
        delivery: '3-4 weeks',
        includes: ['Ideal customer and offer positioning', 'Messaging and sales scripts', 'Channel and funnel strategy', 'Launch roadmap'],
        image: media.services.gtm
    },
    {
        index: '(05)',
        slug: 'meta-ads',
        title: 'Meta Ads.',
        price: 'Custom quote',
        description: "Facebook and Instagram campaigns built to reach the right local customers and turn scrollers into booked calls.",
        delivery: 'Live in 1-2 weeks',
        includes: ['Audience and offer research', 'Ad creative and copy', 'Pixel and conversion tracking', 'Weekly testing and scaling'],
        image: media.services.metaAds
    },
    {
        index: '(06)',
        slug: 'google-ads',
        title: 'Google Ads.',
        price: 'Custom quote',
        description: "Show up the moment someone searches for what you offer, with campaigns tuned for leads, not just clicks.",
        delivery: 'Live in 1-2 weeks',
        includes: ['Keyword and competitor research', 'Search and local campaigns', 'Conversion tracking setup', 'Ongoing bid and budget management'],
        image: media.services.googleAds
    },
    {
        index: '(07)',
        slug: 'web-design',
        title: 'Web Design.',
        price: 'Custom quote',
        description: "Fast, clean websites and landing pages designed to make a strong first impression and turn visitors into leads.",
        delivery: '2-4 weeks',
        includes: ['Custom page designs', 'Mobile-first layouts', 'Lead capture forms', 'On-page SEO basics'],
        image: media.services.webDesign
    },
    {
        index: '(08)',
        slug: 'software-dev',
        title: 'Software Dev.',
        price: 'Custom quote',
        description: "Custom tools, portals and integrations built around how your business actually runs.",
        delivery: 'Scoped per project',
        includes: ['Requirements and scoping', 'Custom web applications', 'Integrations with your tools', 'Support after launch'],
        image: media.services.softwareDev
    }
]

/* Looks a service up by its URL key, for the /services/[slug] page. */
export const getService = (slug: string): Service | undefined => {
    return services.find((service) => service.slug === slug)
}

export const processSteps: ProcessStep[] = [
    {
        index: '(01)',
        title: 'Tell us.',
        copy: "Your name, your business, your city and the best way to reach you. That's it. No credit card, no contract, no 40-question intake form.",
        image: img('process-discovery.png'),
        duration: 'Under a minute',
        items: ['Five quick fields', 'No card, no contract', 'Any local business', 'We reach out right away']
    },
    {
        index: '(02)',
        title: 'We dig in.',
        copy: "We look up your business the way your customers do: on Google, on Maps, in your reviews and in AI answers like ChatGPT. Then we check which competitors are running ads right now, and what they're promising to win your customers.",
        image: img('process-strategy.png'),
        duration: 'Before your call',
        items: ['Your top local competitors', 'Google search and maps', 'Reviews and your website', 'AI answers and competitor ads']
    },
    {
        index: '(03)',
        title: 'Walkthrough.',
        copy: "Giuseppe and Peyton walk you through it on one call: where you rank against your competitors, where leads are slipping away, and the fixes that would move the needle first. No pitch deck. No pressure to buy.",
        image: img('process-design.png'),
        duration: 'One call',
        items: ['Giuseppe and Peyton on the call', 'Where you rank against competitors', 'Your easiest wins, in order', 'No pressure to buy']
    },
    {
        index: '(04)',
        title: 'Your plan.',
        copy: "If we're a fit, you see exactly what we'd build, what it costs and when it goes live. If we're not, you keep the findings and the list of fixes. Either way, you know where you stand.",
        image: img('process-launch.png'),
        duration: 'Yours to keep',
        items: ['The findings, yours to keep', 'A clear list of fixes', 'A package only if it fits', 'Scope and cost upfront']
    }
]

export const principles: Principle[] = [
    {
        index: '01',
        title: 'You talk to the maker.',
        copy: 'No account layer, no handoffs. The person in your calls is the person in the file.'
    },
    {
        index: '02',
        title: 'Scope is set before we start.',
        copy: 'Price, timeline, and deliverables on paper before day one. Surprises are a design flaw.'
    },
    {
        index: '03',
        title: 'We say no early.',
        copy: 'If design is not your real problem, we will tell you in the first call and point you somewhere better.'
    },
    {
        index: '04',
        title: 'You own the work.',
        copy: 'Every file, font, and license comes with the handoff. Nothing stays hostage in our drive.'
    }
]

export const awards: Award[] = [
    { by: 'Awwwards', award: 'Site of the Day', year: '2026' },
    { by: 'FWA', award: 'FWA of the day', year: '2025' },
    { by: 'CSS design awards', award: 'Website of the day', year: '2024' },
    { by: 'Awwwards', award: 'Honorable Mention', year: '2023' }
]

export const studioNumbers = [
    { label: "industries we've shipped in", value: '12' },
    { label: 'people max on any project', value: '3' },
    { label: "longest you'll wait for a reply", value: '24hr' },
    { label: 'of our launches are still live', value: '92%' }
]

export const faqs: Faq[] = [
    {
        question: "What's the difference between monthly and per project?",
        answer: "A project has an end date. We build the brand and the site, hand it over, and it's yours. Membership is for what comes after, or instead of it. $4,500 a month keeps one active request moving, and you pause any month you don't need us."
    },
    {
        question: 'Is that the final price?',
        answer: "It's a real starting number, not a teaser. Scope is the only thing that moves it: an extra page, a second brand direction, a deadline that needs two of us. We lock the figure on the first call and it holds from there."
    },
    {
        question: 'How long will it take?',
        answer: 'Two to four weeks for a single piece of work, four to six for a full brand and site. We only take two clients a month, so your start date is real rather than a spot in a queue.'
    },
    {
        question: 'Who actually does the work?',
        answer: 'The founder. You hire Noma, you get Noma. No juniors, no handoffs, no account manager relaying your feedback.'
    },
    {
        question: 'What do you need from me to start?',
        answer: 'A short brief and someone who can make decisions. The clearer you are on the goal, the faster we move.'
    },
    {
        question: 'Do you offer support after launch?',
        answer: 'Every build ships with 30 days of post-launch support. After that, membership keeps us on call at $4,500 a month. Cancel it the month you stop needing us.'
    }
]

export const contactSteps = [
    {
        index: '01',
        title: 'Pick a time that works.',
        copy: "Thirty minutes on Google Meet. The calendar only shows times we're both free."
    },
    {
        index: '02',
        title: 'We look at your business together.',
        copy: 'Where you show up on Google and Maps, what your competitors are running, and where leads are slipping away.'
    },
    {
        index: '03',
        title: 'You leave with a plan.',
        copy: "The first fixes we'd make and how fast they'd go live. No pitch deck. No pressure to buy."
    }
]

export const addOns = [
    { label: 'Motion design', price: 1000 },
    { label: 'Illustration & brand assets', price: 800 },
    { label: 'Photography direction', price: 1200 },
    { label: 'Pitch deck or sales kit', price: 900 }
]

/* The clients' own words, from their video testimonials. Do not edit or add
   to the text or the figures. A client with no stats hides the stats row. */
export const testimonials: Testimonial[] = [
    {
        name: 'Austin M.',
        role: 'Owner, One Percent Fitness',
        quote: "I can generally tell who's there just to make a dollar and who actually cares.",
        body: 'I personally do lead ads, and anywhere from 77 cents all the way to $1.10 is what it has cost me per lead, which is a phenomenal number, because sometimes I was spending almost four or five dollars per lead.',
        avatar: '/testimonials/avatar-01-austin.jpg',
        video: '/testimonials/ss-testimonial-01-austin-web.mp4',
        poster: '/testimonials/ss-testimonial-01-austin-poster.jpg',
        stats: [
            { label: 'leads in 6 months', value: '1,623' },
            { label: 'average cost per lead', value: '$1.06' }
        ]
    },
    {
        name: 'Reese',
        role: 'Esthetician, Esthetics by Reese',
        quote: 'As of the day my site went live, I was able to land 18 appointments within three days.',
        body: "I've had no prior marketing experience and I'm not the best with social media, so I had no clue where to start. Whenever I first talked to Peyton, he was able to ease my nerves and was able to create an amazing website for me.",
        avatar: '/testimonials/avatar-02-reese.jpg',
        video: '/testimonials/ss-testimonial-02-reese-web.mp4',
        poster: '/testimonials/ss-testimonial-02-reese-poster.jpg',
        stats: [
            { label: 'appointments in 3 days', value: '18' },
            { label: 'appointments on the books', value: '25+' }
        ]
    },
    {
        name: 'Michael Z.',
        role: 'Owner, Harvest Christian Health Coaching',
        quote: "I don't want to partner with just a company. I want to partner with real people.",
        body: 'Strength Scaling stood out differently because they actually took the time to get to know me first, instead of just launching into a bunch of empty sales lines.',
        avatar: '/testimonials/avatar-03-michael.jpg',
        video: '/testimonials/ss-testimonial-03-michael-web.mp4',
        poster: '/testimonials/ss-testimonial-03-michael-poster.jpg',
        stats: [
            { label: 'monthly recurring revenue', value: '$17.4K' },
            { label: 'clients on 12-month contracts', value: '29' }
        ]
    },
    {
        name: 'Juana',
        role: 'North Carolina business owner',
        quote: 'I highly recommend them to anyone looking to grow their business or develop what they already have.',
        body: "I've had the privilege of working with Peyton and Giuseppe at Strength Scaling on multiple different projects, from CRM, AI integration, website development, tons and tons of different things that they do.",
        avatar: '/testimonials/avatar-04-juana.jpg',
        video: '/testimonials/ss-testimonial-04-juana-web.mp4',
        poster: '/testimonials/ss-testimonial-04-juana-poster.jpg',
        stats: []
    }
]
